'use client';

import { ArrowLeft, RotateCcw } from 'lucide-react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { getWorkspaceFilingTarget } from '@/features/workspaces/filingTarget';
import { ConversationTemplates } from './ConversationTemplates';
import { FileIntoWorkspaceToggle } from './FileIntoWorkspaceToggle';
import { PromptBuilderChat } from './PromptBuilderChat';
import { useResearchCreationStore } from './researchCreationStore';
import { usePromptBuilderStore } from './promptBuilderStore';
import { routes } from '@/lib/routes';

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const DEFAULT_BUDGET = 10;

/** Translator bound to the `research` namespace, threaded into the module-level
 *  placeholder resolver (not a React component; cannot call the hook). */
type TFn = ReturnType<typeof useTranslations>;

function templatePlaceholder(t: TFn, id: string): string | undefined {
  switch (id) {
    case 'academic':
      return t('guidedResearchCreation.placeholderAcademic');
    case 'business':
      return t('guidedResearchCreation.placeholderBusiness');
    case 'technical':
      return t('guidedResearchCreation.placeholderTechnical');
    case 'creative':
      return t('guidedResearchCreation.placeholderCreative');
    case 'general':
      return t('guidedResearchCreation.placeholderGeneral');
    default:
      return undefined;
  }
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function GuidedResearchCreation() {
  const t = useTranslations('research');
  const router = useRouter();

  /* --- Prompt builder store -------------------------------------- */
  const messages = usePromptBuilderStore((s) => s.messages);

  /* --- Research creation store ------------------------------------ */
  const isCreating = useResearchCreationStore((s) => s.isCreating);
  const createdResearchId = useResearchCreationStore((s) => s.createdResearchId);
  const creationError = useResearchCreationStore((s) => s.error);

  /* --- Store-driven template state -------------------------------- */
  const selectedTemplateId = usePromptBuilderStore((s) => s.selectedTemplateId);

  const hasConversation = messages.length > 0 || selectedTemplateId !== null;

  /* --- Research-in-Workspace (E1) filing toggle ------------------- */
  // Default ON; only rendered (and only honoured at accept time) when an
  // eligible target exists — active workspace, mounted read_write.
  const [fileIntoWorkspace, setFileIntoWorkspace] = React.useState(true);

  /* --- Navigate on successful creation --------------------------- */
  React.useEffect(() => {
    if (createdResearchId) {
      // Reset both stores, then navigate
      usePromptBuilderStore.getState().actions.reset();
      useResearchCreationStore.getState().actions.reset();
      router.push(routes.research(createdResearchId));
    }
  }, [createdResearchId, router]);

  /* --- Load profiles on mount ------------------------------------ */
  React.useEffect(() => {
    usePromptBuilderStore.getState().actions.loadProfiles();
  }, []);

  /* --- Handlers -------------------------------------------------- */
  const handleSelectTemplate = React.useCallback((templateId: string) => {
    usePromptBuilderStore.getState().actions.applyTemplate(templateId);
  }, []);

  const handleAccept = React.useCallback(
    (prompt: string, profile: string, _explanation: string, limits?: { maxNodes: number; maxDepth: number; maxChildren: number }) => {
      const resolvedProfile = profile || 'default';
      // Research-in-Workspace (E1): re-resolve the target at accept time —
      // eligibility may have changed (unmount, workspace switch) since render.
      const filingTarget = fileIntoWorkspace ? getWorkspaceFilingTarget() : null;
      useResearchCreationStore.getState().actions.createResearch(
        prompt,
        resolvedProfile,
        DEFAULT_BUDGET,
        limits,
        filingTarget?.workspaceId,
      );
    },
    [fileIntoWorkspace],
  );

  const handleReset = React.useCallback(() => {
    usePromptBuilderStore.getState().actions.reset();
    useResearchCreationStore.getState().actions.reset();
  }, []);

  /* --- Render ---------------------------------------------------- */
  return (
    <div className="flex h-full flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border px-6 py-4">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-3">
            <Link
              href="/research"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('guidedResearchCreation.backToResearch')}
            </Link>
            <h1 className="font-serif text-xl font-medium text-foreground">
              {t('guidedResearchCreation.title')}
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('guidedResearchCreation.subtitle')}
          </p>
        </div>

        {hasConversation && (
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={isCreating}>
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            {t('guidedResearchCreation.startOver')}
          </Button>
        )}
      </div>

      {/* Creation error banner */}
      {creationError && (
        <div className="border-b border-destructive/20 bg-destructive/10 px-6 py-3 text-sm text-destructive">
          {t('guidedResearchCreation.setupFailed', { error: creationError })}
        </div>
      )}

      {/* Creating overlay */}
      {isCreating && (
        <div className="border-b border-info/20 bg-info/10 px-6 py-3 text-sm text-info">
          {t('guidedResearchCreation.startingResearch')}
        </div>
      )}

      {/* Main content: two-column on desktop, stack on mobile */}
      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* Sidebar: templates */}
        <aside className="w-full shrink-0 overflow-auto border-b border-border p-5 lg:w-80 lg:border-b-0 lg:border-r">
          <ConversationTemplates
            selectedTemplateId={selectedTemplateId}
            onSelectTemplate={handleSelectTemplate}
          />
        </aside>

        {/* Main area: chat */}
        <main className="flex min-h-0 flex-1 flex-col gap-3 p-5">
          {/* Research-in-Workspace (E1): hidden when no eligible workspace. */}
          <FileIntoWorkspaceToggle
            checked={fileIntoWorkspace}
            onChange={setFileIntoWorkspace}
          />
          <PromptBuilderChat
            onAccept={handleAccept}
            placeholder={selectedTemplateId ? templatePlaceholder(t, selectedTemplateId) : undefined}
          />
        </main>
      </div>
    </div>
  );
}

export { GuidedResearchCreation };
